import axios from 'axios';
import { authService } from './auth';

// Always use HTTPS domain
const baseURL = 'https://api.get-code.pro';
console.log('Connecting to API at:', baseURL);

// Create the axios instance
const axiosInstance = axios.create({
    baseURL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    withCredentials: true  // Enable credentials
});

// Simplify request interceptor
axiosInstance.interceptors.request.use(request => {
    const token = localStorage.getItem('adminToken');
    if (token) {
        request.headers['Authorization'] = `Bearer ${token}`;
    }
    return request;
});

// Add response interceptor for better error handling and token refresh
axiosInstance.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't tried to refresh the token yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                // Try to refresh the token
                const newToken = await authService.refreshToken();
                
                // Update the request header with the new token
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                
                // Retry the original request with the new token
                return axiosInstance(originalRequest);
            } catch (refreshError) {
                // If refresh fails, redirect to login
                console.error('Token refresh failed:', refreshError);
                localStorage.removeItem('adminToken');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }

        console.error('API Error:', {
            status: error.response?.status,
            data: error.response?.data,
            config: error.config
        });
        return Promise.reject(error);
    }
);

// Export the API methods
export const adminApi = {
    // Auth
    post: (url, data) => axiosInstance.post(url, data),
    get: (url, config) => axiosInstance.get(url, config),
    put: (url, data) => axiosInstance.put(url, data),
    delete: (url) => axiosInstance.delete(url),

    // Convenience methods for specific endpoints
    login: (credentials) => axiosInstance.post('/api/admin/auth/login', credentials),
    getCodeOwners: async () => {
        try {
            const response = await axiosInstance.get('/api/admin/code-owners');
            console.log('Code owners response:', response.data);
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to fetch code owners');
            }
            return response.data.data;
        } catch (error) {
            console.error('Error fetching code owners:', error);
            throw error;
        }
    },
    getCodesHistory: async (filters) => {
        try {
            const response = await axiosInstance.get('/api/admin/codes-history', {
                params: filters
            });
            console.log('Codes history response:', response.data);
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to fetch codes history');
            }
            return response.data.data;
        } catch (error) {
            console.error('Error fetching codes history:', error);
            throw error;
        }
    },
    updateCodeOwnerSettings: (ownerId, settings) => 
        axiosInstance.put(`/api/admin/code-owners/${ownerId}`, settings),
    getParameters: () => axiosInstance.get('/api/admin/parameters'),
    updateParameter: (id, data) => axiosInstance.put(`/api/admin/parameters/${id}`, data),
    getUserActivities: async (filters) => {
        try {
            const response = await axiosInstance.get('/api/admin/user-activities', { 
                params: filters 
            });
            console.log('User activities response:', response.data);
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to fetch user activities');
            }
            return response.data.data;
        } catch (error) {
            console.error('Error fetching user activities:', error);
            throw error;
        }
    },
    getDashboardStats: async () => {
        try {
            const response = await axiosInstance.get('/api/admin/dashboard/stats');
            console.log('Dashboard stats response:', response.data);
            
            // Add data validation
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to fetch dashboard stats');
            }
            
            // Ensure arrays exist with defaults
            const data = {
                ...response.data.data,
                dailyClicks: response.data.data.dailyClicks || [],
                topCodes: response.data.data.topCodes || []
            };
            
            return data;
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
            throw error;
        }
    },
    updateOwnerFreeCodes: async (ownerId, freeCodes) => {
        try {
            const response = await axiosInstance.put(`/api/admin/code-owners/${ownerId}/free-codes`, {
                freeCodes
            });
            
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to update free codes');
            }
            
            return response.data;
        } catch (error) {
            console.error('Error updating free codes:', error);
            throw error;
        }
    },

    // Prompts
    getPrompts: () => axiosInstance.get('/api/admin/prompts'),
    updatePrompt: (id, data) => axiosInstance.put(`/api/admin/prompts/${id}`, data),
    createPrompt: (data) => axiosInstance.post('/api/admin/prompts', data),

    // Cities
    getCities: async () => {
        try {
            const response = await axiosInstance.get('/api/admin/cities/with-stats');
            return response.data;
        } catch (error) {
            console.error('Error fetching cities:', error);
            throw error;
        }
    },
    updateCitySearchStatus: (id, searchOn) => 
        axiosInstance.put(`/api/admin/cities/${id}/search-status`, { searchOn }),
    getCityInfo: async () => {
        try {
            const response = await axiosInstance.get('/api/admin/cities');
            return response.data;
        } catch (error) {
            console.error('Error fetching city info:', error);
            throw error;
        }
    },

    // Candidates
    getCandidates: (cityCode = null) => {
        const params = cityCode ? { city_code: cityCode } : {};
        return axiosInstance.get('/api/admin/owner-candidates', { params });
    },
    getCandidateLocations: () => axiosInstance.get('/api/admin/owner-candidates/locations'),
    updateCandidate: (id, data) => axiosInstance.put(`/api/admin/owner-candidates/${id}`, data),
    updateCandidateAgency: (id, isAgency) => axiosInstance.put(`/api/admin/owner-candidates/${id}/agency`, { is_agency: isAgency }),
    approveCandidate: async (id) => {
        try {
            const response = await axiosInstance.post(`/api/admin/owner-candidates/${id}/approve`);
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to approve candidate');
            }
            return response.data;
        } catch (error) {
            console.error('Error approving candidate:', error);
            throw error;
        }
    },
    blacklistCandidate: (id, reason) => 
        axiosInstance.post(`/api/admin/owner-candidates/${id}/blacklist`, { reason }),
    deleteCandidate: (id) => axiosInstance.delete(`/api/admin/owner-candidates/${id}`),
    sendCodeOwnerEmail: async (id, templateId = 1, options = {}) => {
        try {
            const bcc = typeof options === 'boolean' ? options : Boolean(options?.bcc || options?.bccRegister);
            const response = await axiosInstance.post(`/api/admin/owner-candidates/${id}/send-email`, {
                templateId,
                bcc
            });
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to send email');
            }
            return response.data;
        } catch (error) {
            console.error('Error sending email:', error);
            throw error;
        }
    },

    // Email Reach & Verification Statistics
    getEmailReachStats: async (cityCode = null) => {
        try {
            const params = cityCode && cityCode !== 'all' ? { city_code: cityCode } : {};
            const response = await axiosInstance.get('/api/admin/email-reach-stats', { params });
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to fetch email reach statistics');
            }
            return response.data.data;
        } catch (error) {
            console.error('Error fetching email reach stats:', error);
            throw error;
        }
    },

    // Constants for email template IDs
    EMAIL_TEMPLATES: {
        WELCOME: 1,
        AGENCY_PITCH: 2,
        REMINDER: 3,
        REMINDER_AGENCY: 4,
        FINAL_CHECK: 5,
        FINAL_CHECK_AGENCY: 6
    },

    // Helper functions for email status
    isEmailValid: (status) => {
        return ['delivered', 'sent', 'open', 'click'].includes(status);
    },

    isEmailInvalid: (status) => {
        return status === 'invalid_email' || status === 'failed';
    },

    getEmailStatusDetails: (status) => {
        switch (status) {
            case 'invalid_email':
                return {
                    isValid: false,
                    message: 'Invalid email address'
                };
            case 'failed':
                return {
                    isValid: false,
                    message: 'Email delivery failed'
                };
            case 'delivered':
                return {
                    isValid: true,
                    message: 'Email delivered successfully'
                };
            case 'sent':
            case 'pending':
                return {
                    isValid: true,
                    message: 'Email delivery in progress'
                };
            default:
                return {
                    isValid: true,
                    message: status || 'Unknown status'
                };
        }
    },

    // Get email status for a candidate
    getEmailStatus: async (candidateId) => {
        try {
            const response = await axiosInstance.get(`/api/admin/owner-candidates/${candidateId}/email-status`);
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to get email status');
            }
            return response.data.data;
        } catch (error) {
            console.error('Error getting email status:', error);
            throw error;
        }
    },

    // Search code owner by email, owner ID, or code
    searchCodeOwner: async (searchParams) => {
        try {
            const response = await axiosInstance.get('/api/admin/code-owners/search', {
                params: searchParams
            });
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to search code owner');
            }
            return response.data;
        } catch (error) {
            console.error('Error searching code owner:', error);
            throw error;
        }
    },

    getCodeOwnerDeletionInfo: async (ownerId) => {
        try {
            const response = await axiosInstance.get(`/api/admin/code-owners/${ownerId}/deletion-info`);
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to get deletion info');
            }
            return response.data;
        } catch (error) {
            console.error('Error getting deletion info:', error);
            throw error;
        }
    },

    deleteCodeOwner: async (ownerId) => {
        try {
            const response = await axiosInstance.delete(`/api/admin/code-owners/${ownerId}`);
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to delete code owner');
            }
            return response.data;
        } catch (error) {
            console.error('Error deleting code owner:', error);
            throw error;
        }
    },

    // Testing Console
    sendTestEmail: async (email, templateId) => {
        try {
            const response = await axiosInstance.post('/api/admin/test-email', {
                email,
                templateId
            });
            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to send test email');
            }
            return response.data;
        } catch (error) {
            console.error('Error sending test email:', error);
            throw error;
        }
    },

    // In-house Scouting Agent
    runScout: async (cityCode, count = 10, customPrompt = null) => {
        try {
            const response = await axiosInstance.post('/api/admin/scout/run', {
                cityCode,
                count,
                customPrompt
            }, {
                timeout: 300000 // 5 minutes for deep crawling and AI verification
            });
            return response.data;
        } catch (error) {
            console.error('Error running scout:', error);
            throw error;
        }
    },

    runScoutActive: async (candidatesPerCity = 5) => {
        try {
            const response = await axiosInstance.post('/api/admin/scout/run-active', {
                candidatesPerCity
            }, {
                timeout: 300000 // 5 minutes
            });
            return response.data;
        } catch (error) {
            console.error('Error running batch scout:', error);
            throw error;
        }
    },

    getScoutStatus: async () => {
        try {
            const response = await axiosInstance.get('/api/admin/scout/status');
            return response.data;
        } catch (error) {
            console.error('Error fetching scout status:', error);
            throw error;
        }
    },
};
export default adminApi; 
